import { Box, IconButton } from "@mui/material";
import { DotLegalFullScreenSpinner, DotLegalInformationTooltip, DotLegalLink, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import ActivityForm from "../activityForm/ActivityForm";
import PaneLayout, { PaneTitle } from "../../main/panes/paneLayout/PaneLayout";
import { usePaneLayoutStyles } from "../../main/panes/paneLayout/PaneLayout.styles";
import { definePane } from "../../main/panes/definePane";
import { ActivityPaneProps } from "./ActivityPane.types";
import { useActivityPane } from "./ActivityPane.hooks";
import { taskManagementIcon } from "@common/icons";
import { useUrlProvider } from "../../useUrlProvider";

function ActivityPane(props: ActivityPaneProps) {
    const { translateString } = useTranslation();
    const paneLayoutStyles = usePaneLayoutStyles();
    const { getContractOverviewUrl } = useUrlProvider();
    const {
        isLoading,
        isReadOnly,
        inProgress,
        getSaveButtonText,
        firstInvalidSection,
        validationAttempt,
        onSaveClick,
        onCancelClick,
        model,
        onModelChange,
        onScheduleChange,
        showFormError,
        annualWheelActivityId,
        contractInfo,
        showTrialWarning,
        showLegalEntitySelector,
        templateRecurringInterval,
        templateRecurringIntervalType,
        taskManagementUrl,
    } = useActivityPane(props);

    const titleKey = props.mode === "read" ? "seeActivity" : props.annualWheelActivityId ? "editActivity" : "createActivity";
    // The layout keeps a title to one line, but the contract name is the point of this one and the rail is
    // too narrow to fit it beside the rest, so it wraps rather than being cut off.
    const header = (
        <Box sx={{ whiteSpace: "normal", display: "flex", alignItems: "center", gap: 1 }}>
            <PaneTitle>
                {translateString(titleKey)}
                {contractInfo && (
                    <>
                        {" - "}
                        {translateString("obligation")}
                        {" - "}
                        <DotLegalLink linkColor="primary" to={getContractOverviewUrl(contractInfo.id)}>
                            {contractInfo.name}
                        </DotLegalLink>
                    </>
                )}
            </PaneTitle>
            {titleKey === "createActivity" && <DotLegalInformationTooltip text={translateString("createAnnualWheelActivityInfoText")} />}
        </Box>
    );

    const taskManagementAction = taskManagementUrl ? (
        // The icon sits at the window's right edge, where a tooltip centred below it overflows and flashes a scroll bar.
        <DotLegalTooltip text={translateString("viewActivityTasksInTaskManagement")} position="left">
            {/* Flex, so the icon isn't set on a text line whose descender space would make the header taller. */}
            <Box component="span" sx={{ display: "flex" }}>
                <DotLegalLink to={taskManagementUrl}>
                    <IconButton size="small" sx={paneLayoutStyles.headerIconButton}>
                        {taskManagementIcon}
                    </IconButton>
                </DotLegalLink>
            </Box>
        </DotLegalTooltip>
    ) : undefined;

    if (isLoading) {
        return (
            <PaneLayout header={header} actions={taskManagementAction}>
                <DotLegalFullScreenSpinner />
            </PaneLayout>
        );
    }

    return (
        <PaneLayout
            actions={taskManagementAction}
            header={header}
            // Nothing to save or discard when the activity is only being read, so the pair of buttons collapses
            // to the one thing left to do with the pane.
            okButtonText={isReadOnly ? undefined : getSaveButtonText()}
            onOkClick={onSaveClick}
            inProgress={inProgress}
            secondaryButtonText={isReadOnly ? translateString("close") : undefined}
            onSecondaryButtonClick={onCancelClick}
        >
            <ActivityForm
                model={model}
                onModelChange={onModelChange}
                onScheduleChange={onScheduleChange}
                showError={showFormError}
                firstInvalidSection={firstInvalidSection}
                validationAttempt={validationAttempt}
                readOnly={isReadOnly}
                annualWheelActivityId={annualWheelActivityId}
                contractInfo={contractInfo}
                showLegalEntitySelector={showLegalEntitySelector}
                showTrialWarning={showTrialWarning}
                templateRecurringInterval={templateRecurringInterval}
                templateRecurringIntervalType={templateRecurringIntervalType}
            />
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: ActivityPane });
